/**
 * Shared rules for the activity session system.
 *
 * A session is one engaged visit in one tab. The client accrues engaged time and reports it as an
 * absolute total rather than a delta, which keeps every update idempotent: a dropped or retried
 * request is repaired by the next one instead of double-counting.
 *
 * Engaged time is wall-clock time measured from timestamps, never from counting timer ticks, because
 * browsers throttle timers in hidden tabs to roughly once a minute.
 */

export const activityHeartbeatInterval = 30_000

/** Engagement ends once the tab goes hidden or this long passes without any interaction. */
export const activityIdleThreshold = 60_000

/** A shorter pause resumes the same session; a longer gap starts a new one. */
export const activitySessionIdleTimeout = 30 * 60_000

export const activityMaxSessionDuration = 8 * 60 * 60_000

export const activityMaxSessionSeconds = activityMaxSessionDuration / 1000

/** Slack for clock skew between the client and the server, and for request latency. */
const clockSkewGraceSeconds = 60

/**
 * Durations only ever move forwards, and can never exceed the time that has actually passed since the
 * session was created, so a client cannot inflate its own activity by reporting an arbitrary number.
 */
export function resolveActiveDuration(args: { reported: number; stored: number; sessionAgeSeconds: number }): number {
	const sessionAgeSeconds = Number.isFinite(args.sessionAgeSeconds) ? Math.floor(args.sessionAgeSeconds) : 0
	const ceiling = Math.min(Math.max(sessionAgeSeconds, 0) + clockSkewGraceSeconds, activityMaxSessionSeconds)
	const reported = Number.isFinite(args.reported) ? Math.floor(args.reported) : 0
	return Math.max(args.stored, Math.min(Math.max(reported, 0), ceiling))
}
